import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../../../hooks/useAuthContext';
import PeopleDirectory from '../../../features/people/PeopleDirectory';
import { getAllUsers } from '../../../../services/firebase';
import { isAdmin } from '../../../../utils/adminUtils';
import { UsersIcon } from '@heroicons/react/24/outline';

const PeopleTab = () => {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  
  // State management for users data, loading, and error states
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUserAdmin, setIsUserAdmin] = useState(false);

  // Check admin status
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (user?.uid) {
        const adminStatus = await isAdmin(user.uid);
        setIsUserAdmin(adminStatus);
      }
    };
    checkAdminStatus();
  }, [user]);

  const adminEmail = isUserAdmin ? user?.email : null;

  // Redirect if not authenticated
  useEffect(() => {
    if (!user) {
      navigate('/');
    }
  }, [user, navigate]);

  // Data fetching using getAllUsers() function
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // getAllUsers now always excludes hidden users - hidden users are fetched separately
      const result = await getAllUsers();
      
      if (result.success) {
        setUsers(result.data);
      } else {
        setError(result.error || 'Failed to load users');
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('An unexpected error occurred while loading users');
    } finally {
      setLoading(false);
    }
  };

  // Fetch users on component mount
  useEffect(() => {
    if (user) {
      fetchUsers();
    }
  }, [user]);

  // Retry function for error handling
  const handleRetry = () => {
    fetchUsers();
  };

  // Handle users update (for admin actions)
  const handleUsersUpdate = async () => {
    await fetchUsers();
  };

  if (!user) {
    return (
      <div className="py-12 flex justify-center">
        <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-amber-600 border-r-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Burnt Network</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          See who's in the Burnt network. DMs and other features coming soon...
        </p>
      </div>

      {/* People Directory Component */}
      <PeopleDirectory 
        users={users}
        loading={loading}
        error={error}
        onRetry={handleRetry}
        isAdmin={isUserAdmin}
        adminEmail={adminEmail}
        onUsersUpdate={handleUsersUpdate}
      />
    </div>
  );
};

export default PeopleTab; 